import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";

const SLIDES = [
  {
    key: "intro",
    type: "intro",
    theme: "light",
    background: "https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=1920&q=80",
    eyebrow: "we guide you",
    title: "Across Indonesia",
  },
  {
    key: "yogyakarta",
    type: "destination",
    theme: "dark",
    background: "https://images.unsplash.com/photo-1596402184320-417e7178b2cd?auto=format&fit=crop&w=1920&q=80",
    title: "Yogyakarta",
    body: "The whispering courtyards of the Sultan, the slow batik of Kotagede, the dawn light at Borobudur — none of it has hurried in a thousand years.",
    cards: [
      { tag: "TEMPLE", title: "Borobudur", img: "https://images.unsplash.com/photo-1596402184320-417e7178b2cd?auto=format&fit=crop&w=600&q=80" },
      { tag: "ROYAL", title: "Kraton", img: "https://images.unsplash.com/photo-1555400038-63f5ba517a47?auto=format&fit=crop&w=600&q=80" },
      { tag: "CRAFT", title: "Kotagede", img: "https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=600&q=80" },
      { tag: "BEACH", title: "Parangtritis", img: "https://images.unsplash.com/photo-1542897644-e04428948020?auto=format&fit=crop&w=600&q=80" },
    ],
  },
  {
    key: "bali",
    type: "destination",
    theme: "dark",
    background: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1920&q=80",
    title: "Bali",
    body: "The bright green rice terraces, the monkey jungles in the mountains, the black sand coastline, the sunrise and sunsets on volcanoes — all of them are more beautiful than each other.",
    cards: [
      { tag: "MONKEY FOREST", title: "Ubud", img: "https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=600&q=80" },
      { tag: "NIGHT",         title: "Denpasar", img: "https://images.unsplash.com/photo-1518509562904-e7ef99cddc85?auto=format&fit=crop&w=600&q=80" },
      { tag: "ROCK MOUNTAIN", title: "Nusa Penida", img: "https://images.unsplash.com/photo-1583245177184-4c46d5963fd5?auto=format&fit=crop&w=600&q=80" },
      { tag: "SUNSET",        title: "Kuta Beach", img: "https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?auto=format&fit=crop&w=600&q=80" },
    ],
  },
  {
    key: "komodo",
    type: "destination",
    theme: "light",
    background: "https://images.unsplash.com/photo-1604560929658-bbc3c2ba6a36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    title: "Komodo",
    body: "A Phinisi at first light, manta wings in the channel, and a stillness only this archipelago seems to know. Dragons of Rinca and the pink sands of Padar.",
    cards: [
      { tag: "VOLCANO", title: "Padar",       img: "https://images.unsplash.com/photo-1604560929658-bbc3c2ba6a36?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { tag: "PINK",    title: "Pink Beach",  img: "https://images.pexels.com/photos/10482395/pexels-photo-10482395.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=700" },
      { tag: "REEF",    title: "Manta Point", img: "https://images.unsplash.com/photo-1582967788606-a171c1080cb0?auto=format&fit=crop&w=700&q=80" },
      { tag: "DRAGONS", title: "Rinca",       img: "https://images.unsplash.com/photo-1588842367425-8270e4b3002c?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
  {
    key: "raja-ampat",
    type: "destination",
    theme: "dark",
    background: "https://images.pexels.com/photos/10482395/pexels-photo-10482395.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1900",
    title: "Raja Ampat",
    body: "Limestone karsts rising out of a glass-still sea, world-class reef, the quietest mornings of our lives. The four kings of West Papua.",
    cards: [
      { tag: "VIEWPOINT", title: "Piaynemo",   img: "https://images.pexels.com/photos/10482395/pexels-photo-10482395.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=700" },
      { tag: "REEF",      title: "Arborek",    img: "https://images.unsplash.com/photo-1582967788606-a171c1080cb0?auto=format&fit=crop&w=700&q=80" },
      { tag: "KAYAK",     title: "Wayag",      img: "https://images.unsplash.com/photo-1583245177184-4c46d5963fd5?auto=format&fit=crop&w=700&q=80" },
      { tag: "VILLAGE",   title: "Sawinggrai", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
];

// Broadcast active slide theme so Navbar can adapt
export const setSliderTheme = (theme) => {
  document.documentElement.dataset.heroTheme = theme || "dark";
};

export default function HomeSlider() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [animating, setAnimating] = useState(false);

  const bgRef = useRef(null);
  const overlayRef = useRef(null);
  const introRef = useRef(null);
  const destRef = useRef(null);
  const cardsRef = useRef([]);

  const slide = SLIDES[index];

  // Sync theme to <html data-hero-theme="..."> so Navbar can adapt
  useEffect(() => {
    setSliderTheme(slide.theme);
    return () => setSliderTheme(null);
  }, [slide.theme]);

  const goTo = (next) => {
    if (animating) return;
    const wrapped = (next + SLIDES.length) % SLIDES.length;
    if (wrapped === index) return;
    setDirection(wrapped > index || (wrapped === 0 && index === SLIDES.length - 1) ? 1 : -1);
    setIndex(wrapped);
  };

  // Elegant transition each slide change
  useEffect(() => {
    setAnimating(true);
    const dir = direction;
    const tl = gsap.timeline({ onComplete: () => setAnimating(false) });

    if (bgRef.current) {
      const from = dir > 0 ? "inset(0 100% 0 0)" : "inset(0 0 0 100%)";
      tl.fromTo(
        bgRef.current,
        { clipPath: from, scale: 1.08, filter: "brightness(0.85)" },
        { clipPath: "inset(0 0% 0 0%)", scale: 1, filter: "brightness(1)", duration: 1.1, ease: "power3.inOut" },
        0
      );
    }

    if (overlayRef.current) {
      tl.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.7, ease: "power2.out" }, 0.2);
    }

    const content = slide.type === "intro" ? introRef.current : destRef.current;
    if (content) {
      tl.fromTo(
        content.querySelectorAll("[data-anim]"),
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: "power3.out" },
        0.45
      );
    }

    if (slide.type === "destination" && cardsRef.current.length) {
      tl.fromTo(
        cardsRef.current.filter(Boolean),
        { y: 80, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.65, stagger: 0.09, ease: "power3.out" },
        0.6
      );
    }
    return () => tl.kill();
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goTo(index + 1);
      if (e.key === "ArrowLeft") goTo(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section data-testid="home-slider" className="relative bg-[#EFEAE2]">
      {/* Image card — not fullscreen, sits between nav and CTA bar with side padding */}
      <div className="relative h-[88vh] min-h-[640px] overflow-hidden rounded-none">
        {/* Background image */}
        <div
          ref={bgRef}
          key={slide.key + "-bg"}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${slide.background})`, willChange: "clip-path, transform" }}
        />
        <div
          ref={overlayRef}
          className={`absolute inset-0 ${
            slide.type === "intro"
              ? "bg-gradient-to-b from-white/5 via-white/10 to-white/30"
              : "bg-gradient-to-r from-black/40 via-black/15 to-black/50"
          }`}
        />

        {/* Top gradient strip — ensures navbar always has a darker zone for contrast */}
        <div className="pointer-events-none absolute top-0 inset-x-0 h-28 z-[5] bg-gradient-to-b from-black/35 via-black/15 to-transparent" />

        {/* Slide content */}
        {slide.type === "intro" ? (
          <div ref={introRef} className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
            {/* Soft cloud-shaped backdrop behind the title — organic, multiple blurred blobs */}
            <div data-anim aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative w-[1100px] max-w-[95vw] h-[460px]">
                {/* Layer 1 — wide central glow */}
                <div className="absolute inset-0"
                     style={{
                       background:
                         "radial-gradient(ellipse 60% 55% at 50% 55%, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.55) 22%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0) 75%)",
                       filter: "blur(22px)",
                     }} />
                {/* Layer 2 — left puff */}
                <div className="absolute top-[14%] left-[6%] w-[44%] h-[70%]"
                     style={{
                       background:
                         "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.0) 70%)",
                       filter: "blur(28px)",
                       transform: "rotate(-6deg)",
                     }} />
                {/* Layer 3 — right puff */}
                <div className="absolute top-[10%] right-[4%] w-[48%] h-[75%]"
                     style={{
                       background:
                         "radial-gradient(ellipse 65% 55% at 50% 50%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.0) 70%)",
                       filter: "blur(32px)",
                       transform: "rotate(4deg)",
                     }} />
                {/* Layer 4 — top wisp */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-[80%] h-[30%]"
                     style={{
                       background:
                         "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.0) 75%)",
                       filter: "blur(26px)",
                     }} />
                {/* Layer 5 — bottom wisp */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[88%] h-[28%]"
                     style={{
                       background:
                         "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.0) 75%)",
                       filter: "blur(24px)",
                     }} />
              </div>
            </div>

            <div data-anim className="relative text-lg md:text-2xl text-slate-900 font-script italic" style={{textShadow: "0 1px 2px rgba(255,255,255,0.7)"}}>
              {slide.eyebrow}
            </div>
            <h1 data-anim className="relative font-script font-bold text-slate-900 text-7xl md:text-9xl lg:text-[10rem] mt-1 leading-none" style={{textShadow: "0 2px 18px rgba(255,255,255,0.55)"}}>
              {slide.title}
            </h1>
          </div>
        ) : (
          <div ref={destRef} className="relative z-10 h-full grid grid-cols-1 md:grid-cols-12 gap-6 px-6 md:px-32 lg:px-40 pt-24 pb-12">
            <div className="md:col-start-7 md:col-span-6 lg:col-start-8 lg:col-span-5 self-start mt-8 md:mt-20 text-white max-w-xl md:ml-auto md:pr-4">
              <h2 data-anim className="font-script font-bold text-white text-6xl md:text-7xl lg:text-8xl leading-none drop-shadow-lg">
                {slide.title}
              </h2>
              <p data-anim className="mt-6 text-sm md:text-base text-white/90 leading-relaxed max-w-md drop-shadow">
                {slide.body}
              </p>
              <button
                data-anim
                data-testid={`slide-explore-${slide.key}`}
                onClick={() => navigate("/tours")}
                className="mt-6 inline-flex items-center gap-2 text-sm text-white tracking-wider hover:text-white/80 border-b border-white/40 hover:border-white pb-1 transition-colors"
              >
                explore more <ArrowUpRight size={14} />
              </button>
            </div>

            <div className="md:col-start-5 md:col-span-8 lg:col-start-6 lg:col-span-7 self-end flex items-end justify-end gap-3 md:gap-4 pb-2 md:pr-4">
              {slide.cards.map((c, i) => (
                <div
                  key={`${slide.key}-${i}`}
                  ref={(el) => (cardsRef.current[i] = el)}
                  data-testid={`slide-card-${slide.key}-${i}`}
                  className="group relative shrink-0 rounded-[36px] overflow-hidden ring-1 ring-white/20 shadow-2xl cursor-pointer
                             w-[120px] md:w-[135px] h-[240px] md:h-[270px]
                             hover:w-[148px] md:hover:w-[170px] hover:h-[280px] md:hover:h-[330px]
                             transition-all duration-500 ease-out"
                  onClick={() => navigate("/tours")}
                >
                  <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-3 right-3 text-white">
                    <div className="text-[9px] tracking-[0.22em] uppercase opacity-85">{c.tag}</div>
                    <div className="mt-0.5 text-base md:text-lg font-script font-bold leading-tight">{c.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Left arrow */}
        <button
          data-testid="slider-prev"
          onClick={() => goTo(index - 1)}
          aria-label="Previous slide"
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/80 hover:bg-white text-slate-900 backdrop-blur flex items-center justify-center shadow-lg transition-all hover:-translate-x-0.5"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>

        {/* Right arrow */}
        <button
          data-testid="slider-next"
          onClick={() => goTo(index + 1)}
          aria-label="Next slide"
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/80 hover:bg-white text-slate-900 backdrop-blur flex items-center justify-center shadow-lg transition-all hover:translate-x-0.5"
        >
          <ArrowRight size={18} strokeWidth={1.5} />
        </button>

        {/* Slide dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              data-testid={`slider-dot-${s.key}`}
              onClick={() => goTo(i)}
              aria-label={s.title}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === index ? "w-8 bg-white" : "w-1.5 bg-white/50 hover:bg-white/75"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Bottom CTA strip (separate from image, sits at bottom of section) */}
      <div className="bg-[#EFEAE2] border-t border-stone-300/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm md:text-base text-slate-700 font-script italic">
            Ready for your next journey in Indonesia ?
          </div>
          <button
            data-testid="slider-book-consult"
            onClick={() => navigate("/consultations")}
            className="text-sm md:text-base text-slate-900 font-medium hover:text-seafoam transition-colors inline-flex items-center gap-1"
          >
            book a consultation <span className="text-lg">+</span>
          </button>
        </div>
      </div>
    </section>
  );
}
